import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: { padding: 40, fontSize: 10, fontFamily: 'Helvetica', color: '#333' },
  header: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 30 },
  title: { fontSize: 24, fontWeight: 'bold', color: '#000' },
  invoiceNumber: { fontSize: 12, color: '#666', marginTop: 4 },
  section: { marginBottom: 15 },
  sectionTitle: { fontSize: 8, color: '#999', textTransform: 'uppercase', marginBottom: 4, letterSpacing: 1 },
  bold: { fontWeight: 'bold', fontFamily: 'Helvetica-Bold' },
  row: { flexDirection: 'row' },
  // Table
  tableHeader: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#ccc', paddingBottom: 6, marginBottom: 6 },
  tableRow: { flexDirection: 'row', paddingVertical: 4, borderBottomWidth: 0.5, borderBottomColor: '#eee' },
  colDescription: { flex: 3 },
  colQty: { flex: 1, textAlign: 'center' },
  colPrice: { flex: 1, textAlign: 'right' },
  colAmount: { flex: 1, textAlign: 'right' },
  // Totals
  totalsContainer: { marginTop: 10, alignItems: 'flex-end' },
  totalRow: { flexDirection: 'row', width: 200, justifyContent: 'space-between', paddingVertical: 2 },
  grandTotal: { flexDirection: 'row', width: 200, justifyContent: 'space-between', paddingVertical: 4, borderTopWidth: 1, borderTopColor: '#333', marginTop: 4 },
  grandTotalText: { fontSize: 14, fontWeight: 'bold', fontFamily: 'Helvetica-Bold' },
  // Footer
  footer: { position: 'absolute', bottom: 30, left: 40, right: 40, textAlign: 'center', fontSize: 8, color: '#999' },
  notes: { marginTop: 20, padding: 10, backgroundColor: '#f9f9f9', borderRadius: 4 },
  dates: { flexDirection: 'row', gap: 30, marginBottom: 20 },
  logo: { width: 60, height: 60, objectFit: 'contain' },
});

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount || 0);

const formatAddress = (addr) => {
  if (!addr) return null;
  return [addr.street, addr.city, addr.state, addr.postcode].filter(Boolean).join(', ');
};

const ClassicTemplate = ({ invoice }) => {
  const issuer = invoice.issuerSnapshot || {};
  const org = invoice.organisationSnapshot || {};

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            {issuer.logoUrl && <Image src={issuer.logoUrl} style={styles.logo} />}
            <View>
              <Text style={styles.title}>INVOICE</Text>
              <Text style={styles.invoiceNumber}>{invoice.invoiceNumber}</Text>
            </View>
          </View>
          <View style={{ textAlign: 'right' }}>
            <Text style={styles.bold}>{issuer.businessName || issuer.name || ''}</Text>
            {issuer.abn && <Text>ABN: {issuer.abn}</Text>}
            {issuer.email && <Text>{issuer.email}</Text>}
            {issuer.phone && <Text>{issuer.phone}</Text>}
            {formatAddress(issuer.address) && <Text>{formatAddress(issuer.address)}</Text>}
          </View>
        </View>

        {/* Bill To */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Bill To</Text>
          <Text style={styles.bold}>{org.name || ''}</Text>
          {org.abn && <Text>ABN: {org.abn}</Text>}
          {org.contactPerson && <Text>Attn: {org.contactPerson}</Text>}
          {org.email && <Text>{org.email}</Text>}
          {formatAddress(org.address) && <Text>{formatAddress(org.address)}</Text>}
        </View>

        {/* Dates */}
        <View style={styles.dates}>
          <View>
            <Text style={styles.sectionTitle}>Issue Date</Text>
            <Text>{invoice.issueDate ? new Date(invoice.issueDate).toLocaleDateString('en-AU') : ''}</Text>
          </View>
          <View>
            <Text style={styles.sectionTitle}>Due Date</Text>
            <Text>{invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString('en-AU') : ''}</Text>
          </View>
        </View>

        {/* Line Items Table */}
        <View style={styles.tableHeader}>
          <Text style={[styles.colDescription, styles.bold]}>Description</Text>
          <Text style={[styles.colQty, styles.bold]}>Qty</Text>
          <Text style={[styles.colPrice, styles.bold]}>Unit Price</Text>
          <Text style={[styles.colAmount, styles.bold]}>Amount</Text>
        </View>
        {(invoice.lineItems || []).map((item, idx) => (
          <View key={idx} style={styles.tableRow}>
            <Text style={styles.colDescription}>{item.description}</Text>
            <Text style={styles.colQty}>{item.quantity}</Text>
            <Text style={styles.colPrice}>{formatCurrency(item.unitPrice)}</Text>
            <Text style={styles.colAmount}>{formatCurrency(item.amount)}</Text>
          </View>
        ))}

        {/* Totals */}
        <View style={styles.totalsContainer}>
          <View style={styles.totalRow}>
            <Text>Subtotal</Text>
            <Text>{formatCurrency(invoice.subtotal)}</Text>
          </View>
          {invoice.gstIncluded && (
            <View style={styles.totalRow}>
              <Text>GST ({invoice.gstRate || 10}%)</Text>
              <Text>{formatCurrency(invoice.gstAmount)}</Text>
            </View>
          )}
          <View style={styles.grandTotal}>
            <Text style={styles.grandTotalText}>Total ({invoice.currency || 'AUD'})</Text>
            <Text style={styles.grandTotalText}>{formatCurrency(invoice.total)}</Text>
          </View>
        </View>

        {/* Notes */}
        {invoice.notes && (
          <View style={styles.notes}>
            <Text style={styles.sectionTitle}>Notes</Text>
            <Text>{invoice.notes}</Text>
          </View>
        )}

        {/* Bank Details */}
        {issuer.bankDetails && (issuer.bankDetails.bsb || issuer.bankDetails.accountNumber) && (
          <View style={[styles.section, { marginTop: 15 }]}>
            <Text style={styles.sectionTitle}>Payment Details</Text>
            {issuer.bankDetails.bankName && <Text>Bank: {issuer.bankDetails.bankName}</Text>}
            {issuer.bankDetails.accountName && <Text>Account Name: {issuer.bankDetails.accountName}</Text>}
            {issuer.bankDetails.bsb && <Text>BSB: {issuer.bankDetails.bsb}</Text>}
            {issuer.bankDetails.accountNumber && <Text>Account: {issuer.bankDetails.accountNumber}</Text>}
          </View>
        )}

        {issuer.paymentTerms && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Payment Terms</Text>
            <Text>{issuer.paymentTerms}</Text>
          </View>
        )}

        <Text style={styles.footer}>Thank you for your business</Text>
      </Page>
    </Document>
  );
};

export default ClassicTemplate;
